import type { ReactElement } from "react";
import { AlertCircle, CheckCircle2, Clock, Loader2, Upload, XCircle } from "lucide-react";
import type { EncodingStatus } from "../types";

/**
 * Get status badge color and icon based on encoding status
 */
export function getStatusBadge(status: EncodingStatus | null): {
  color: string;
  icon: ReactElement;
  label: string;
} {
  if (!status) {
    return {
      color: "text-neutral-600",
      icon: <XCircle className="h-3 w-3" />,
      label: "Missing",
    };
  }

  switch (status) {
    case "PENDING":
      return {
        color: "text-yellow-400",
        icon: <Clock className="h-3 w-3" />,
        label: "Pending",
      };
    case "UPLOADING":
    case "UPLOADED":
      return {
        color: "text-blue-400",
        icon: <Upload className="h-3 w-3" />,
        label: status === "UPLOADING" ? "Uploading" : "Uploaded",
      };
    case "ENCODING":
      return {
        color: "text-blue-400",
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        label: "Encoding",
      };
    case "READY":
      return {
        color: "text-green-400",
        icon: <CheckCircle2 className="h-3 w-3" />,
        label: "Ready",
      };
    case "ERRORED":
      return {
        color: "text-red-400",
        icon: <AlertCircle className="h-3 w-3" />,
        label: "Error",
      };
  }
}

/**
 * Format duration in seconds to human-readable format
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds) return "—";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${seconds % 60}s`;
}
